import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { createPortal } from "react-dom";
import Swal from "sweetalert2";
import { FiSearch } from "react-icons/fi";
import {
  XMarkIcon,
  CameraIcon,
  TrashIcon,
} from "@heroicons/react/24/outline";
import { CheckIcon } from "@heroicons/react/20/solid";
import { useSeo } from "../useHook";
import { ImageWithFallback } from "../../../../Utils/loader";
import SelectField from "../../components/SelectField";
import {
  ENTITY_PAGE_SIZE,
  mergeEntityPage,
  resolveEntityTotalCount,
  canLoadMoreEntities,
  shouldFetchNextPageOnScroll,
} from "./entityListPagination";

const emptyForm = {
  entity_type: "",
  entity_id: "",
  meta_title: "",
  meta_description: "",
  meta_keywords: "",
  canonical_url: "",
  robots: "index,follow",
  slug: "",
  og_type: "website",
  og_title: "",
  og_description: "",
  og_image: "",
  og_image_alt: "",
};

const ROBOTS_OPTIONS = [
  {
    value: "index,follow",
    label: "Index, Follow",
    hint: "Show page and follow links",
  },
  {
    value: "noindex,follow",
    label: "No Index, Follow",
    hint: "Hide page, still follow links",
  },
  {
    value: "index,nofollow",
    label: "Index, No Follow",
    hint: "Show page, do not follow links",
  },
  {
    value: "noindex,nofollow",
    label: "No Index, No Follow",
    hint: "Hide page and links",
  },
];

const OG_TYPE_OPTIONS = [
  { value: "website", label: "Website" },
  { value: "product", label: "Product" },
  { value: "article", label: "Article" },
];
const OG_IMAGE_WIDTH = 1200;
const OG_IMAGE_HEIGHT = 630;

const REQUIRED_TEXT_FIELDS = [
  { key: "meta_title", label: "Meta Title" },
  { key: "meta_description", label: "Meta Description" },
  { key: "meta_keywords", label: "Meta Keywords" },
  { key: "slug", label: "Slug" },
  { key: "canonical_url", label: "Canonical URL" },
  { key: "robots", label: "Robots" },
  { key: "og_type", label: "OG Type" },
];

const inputClass =
  "w-full h-[48px] px-4 rounded-xl border border-slate-200 bg-white text-sm text-slate-800 placeholder:text-slate-400 outline-none focus:border-brand-primary focus:ring-2 focus:ring-brand-primary/20 transition-all duration-200";
const inputErrorClass =
  "w-full h-[48px] px-4 rounded-xl border border-red-400 bg-white text-sm text-slate-800 placeholder:text-slate-400 outline-none focus:ring-2 focus:ring-red-100 transition-all duration-200";
const textareaClass =
  "w-full min-h-[96px] px-4 py-3 rounded-xl border border-slate-200 bg-white text-sm text-slate-800 placeholder:text-slate-400 outline-none resize-none focus:border-brand-primary focus:ring-2 focus:ring-brand-primary/20 transition-all duration-200";
const textareaErrorClass =
  "w-full min-h-[96px] px-4 py-3 rounded-xl border border-red-400 bg-white text-sm text-slate-800 placeholder:text-slate-400 outline-none resize-none focus:ring-2 focus:ring-red-100 transition-all duration-200";
const REQUIRED_MSG = "This field is required";
const labelClass = "block text-[13px] font-semibold text-slate-700 mb-1.5";

function SeoModal({ isOpen, onClose, editData = null, groupedModules = {}, onSuccess }) {
  const { getEntities, createSeo, updateSeo } = useSeo();
  const editingId = editData?.id || null;

  const [form, setForm] = useState(emptyForm);
  const [fieldErrors, setFieldErrors] = useState({});
  const [entityOptions, setEntityOptions] = useState([]);
  const [entityTotalCount, setEntityTotalCount] = useState(0);
  const [entitySearch, setEntitySearch] = useState("");
  const [selectedEntityLabel, setSelectedEntityLabel] = useState("");
  const [isItemDropdownOpen, setIsItemDropdownOpen] = useState(false);
  const [entitiesLoading, setEntitiesLoading] = useState(false);
  const [entitiesLoadingMore, setEntitiesLoadingMore] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [ogImageFile, setOgImageFile] = useState(null);
  const [ogImagePreview, setOgImagePreview] = useState(null);
  const [ogImageError, setOgImageError] = useState("");
  const itemDropdownRef = useRef(null);
  const itemMenuRef = useRef(null);
  const fetchGenRef = useRef(0);
  const loadingMoreRef = useRef(false);
  const pendingResetRef = useRef(false);
  const entityOptionsRef = useRef([]);
  entityOptionsRef.current = entityOptions;
  const [itemMenuCoords, setItemMenuCoords] = useState({
    top: 0,
    left: 0,
    width: 0,
  });

  const moduleGroups = useMemo(
    () =>
      Object.entries(groupedModules).map(([label, opts]) => ({
        label,
        options: (opts || []).map((m) => ({ value: m.value, label: m.label })),
      })),
    [groupedModules],
  );

  const fieldClass = (key, multiline = false) => {
    if (multiline) {
      return fieldErrors[key] ? textareaErrorClass : textareaClass;
    }
    return fieldErrors[key] ? inputErrorClass : inputClass;
  };

  const setField = (key, value) => {
    setForm((prev) => ({ ...prev, [key]: value }));
    setFieldErrors((prev) => {
      if (!prev[key]) return prev;
      const next = { ...prev };
      delete next[key];
      return next;
    });
  };

  const clearFieldError = (key) => {
    setFieldErrors((prev) => {
      if (!prev[key]) return prev;
      const next = { ...prev };
      delete next[key];
      return next;
    });
  };

  const resetOgImageState = () => {
    setOgImageFile(null);
    setOgImagePreview(null);
    setOgImageError("");
  };

  const resetFormState = () => {
    fetchGenRef.current += 1;
    loadingMoreRef.current = false;
    pendingResetRef.current = false;
    setForm(emptyForm);
    setFieldErrors({});
    setEntityOptions([]);
    setEntityTotalCount(0);
    setEntitySearch("");
    setSelectedEntityLabel("");
    setIsItemDropdownOpen(false);
    setEntitiesLoading(false);
    setEntitiesLoadingMore(false);
    resetOgImageState();
  };

  useEffect(() => {
    if (!isOpen) return;

    if (editData) {
      setForm({
        entity_type: editData.entity_type || "",
        entity_id: editData.entity_id || "",
        meta_title: editData.meta_title || "",
        meta_description: editData.meta_description || "",
        meta_keywords: editData.meta_keywords || "",
        canonical_url: editData.canonical_url || "",
        robots: editData.robots || "index,follow",
        slug: editData.slug || "",
        og_type: editData.og_type || "website",
        og_title: editData.og_title || "",
        og_description: editData.og_description || "",
        og_image: editData.og_image || "",
        og_image_alt: editData.og_image_alt || "",
      });
      setSelectedEntityLabel(editData.entity_label || "");
      setIsItemDropdownOpen(false);
      setOgImageFile(null);
      setOgImagePreview(editData.og_image || null);
      setOgImageError("");
      setFieldErrors({});
    } else {
      resetFormState();
    }
  }, [isOpen, editData]);

  const fetchEntities = useCallback(
    async ({ offset, append, search, generation }) => {
      try {
        const data = await getEntities({
          entityType: form.entity_type,
          search: search || undefined,
          limit: ENTITY_PAGE_SIZE,
          offset,
        });
        if (generation !== fetchGenRef.current) return;

        const items = data.items || [];
        const prevItems = append ? entityOptionsRef.current : [];
        const nextItems = mergeEntityPage(prevItems, items, append);
        setEntityOptions(nextItems);
        entityOptionsRef.current = nextItems;
        setEntityTotalCount(
          resolveEntityTotalCount({
            append,
            prevCount: prevItems.length,
            nextCount: nextItems.length,
            totalCount: data.totalCount,
          }),
        );
        if (!append) pendingResetRef.current = false;
      } catch {
        if (generation !== fetchGenRef.current) return;
        if (!append) {
          setEntityOptions([]);
          entityOptionsRef.current = [];
          setEntityTotalCount(0);
          pendingResetRef.current = false;
        }
      } finally {
        if (generation === fetchGenRef.current) {
          setEntitiesLoading(false);
          setEntitiesLoadingMore(false);
          loadingMoreRef.current = false;
        }
      }
    },
    [form.entity_type, getEntities],
  );

  useEffect(() => {
    if (!isOpen || !form.entity_type || editingId) return undefined;

    pendingResetRef.current = true;
    loadingMoreRef.current = false;
    const generation = ++fetchGenRef.current;

    const timer = setTimeout(() => {
      setEntitiesLoading(true);
      setEntitiesLoadingMore(false);
      fetchEntities({
        offset: 0,
        append: false,
        search: entitySearch.trim(),
        generation,
      });
    }, 300);

    return () => clearTimeout(timer);
  }, [isOpen, form.entity_type, entitySearch, editingId, fetchEntities]);

  const loadMoreEntities = () => {
    if (
      !canLoadMoreEntities({
        entityType: form.entity_type,
        isEditing: Boolean(editingId),
        isLoading: entitiesLoading,
        isLoadingMore: entitiesLoadingMore,
        isPendingReset: pendingResetRef.current,
        loadedCount: entityOptionsRef.current.length,
        totalCount: entityTotalCount,
      }) ||
      loadingMoreRef.current
    ) {
      return;
    }

    loadingMoreRef.current = true;
    setEntitiesLoadingMore(true);
    fetchEntities({
      offset: entityOptionsRef.current.length,
      append: true,
      search: entitySearch.trim(),
      generation: fetchGenRef.current,
    });
  };

  const handleEntityListScroll = (event) => {
    const el = event.currentTarget;
    if (
      shouldFetchNextPageOnScroll({
        scrollTop: el.scrollTop,
        clientHeight: el.clientHeight,
        scrollHeight: el.scrollHeight,
      })
    ) {
      loadMoreEntities();
    }
  };

  useEffect(() => {
    if (!isItemDropdownOpen) return undefined;
    const handleClickOutside = (event) => {
      if (
        itemDropdownRef.current?.contains(event.target) ||
        itemMenuRef.current?.contains(event.target)
      ) {
        return;
      }
      setIsItemDropdownOpen(false);
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [isItemDropdownOpen]);

  useEffect(() => {
    if (!isItemDropdownOpen) return undefined;
    const updateCoords = () => {
      const el = itemDropdownRef.current?.querySelector("input");
      if (!el) return;
      const rect = el.getBoundingClientRect();
      setItemMenuCoords({
        top: rect.bottom + 6,
        left: rect.left,
        width: rect.width,
      });
    };
    updateCoords();
    window.addEventListener("resize", updateCoords);
    window.addEventListener("scroll", updateCoords, true);
    return () => {
      window.removeEventListener("resize", updateCoords);
      window.removeEventListener("scroll", updateCoords, true);
    };
  }, [isItemDropdownOpen, entityOptions.length, entitiesLoading]);

  useEffect(() => {
    if (!isOpen) return undefined;
    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      document.body.style.overflow = previousOverflow;
    };
  }, [isOpen]);

  const handleClose = () => {
    resetFormState();
    onClose();
  };

  const handleSelectItem = (item) => {
    if (item.hasSeo) return;
    setForm((prev) => ({ ...prev, entity_id: String(item.id) }));
    clearFieldError("entity_id");
    setSelectedEntityLabel(item.title || `Item ${item.id}`);
    setEntitySearch("");
    setIsItemDropdownOpen(false);
  };

  const handleOgImageChange = (e) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;

    if (!file.type.startsWith("image/")) {
      setOgImageError("Please upload a valid image file.");
      setOgImageFile(null);
      setOgImagePreview(null);
      return;
    }

    if (ogImagePreview?.startsWith("blob:")) {
      URL.revokeObjectURL(ogImagePreview);
    }

    const preview = URL.createObjectURL(file);
    const img = new Image();
    img.onload = () => {
      if (img.width !== OG_IMAGE_WIDTH || img.height !== OG_IMAGE_HEIGHT) {
        URL.revokeObjectURL(preview);
        setOgImageError(
          `Image must be exactly ${OG_IMAGE_WIDTH}x${OG_IMAGE_HEIGHT} pixels.`,
        );
        setOgImageFile(null);
        setOgImagePreview(null);
        return;
      }
      setOgImageError("");
      setOgImageFile(file);
      setOgImagePreview(preview);
      setForm((prev) => ({ ...prev, og_image: "" }));
    };
    img.onerror = () => {
      URL.revokeObjectURL(preview);
      setOgImageError("Invalid image file.");
      setOgImageFile(null);
      setOgImagePreview(null);
    };
    img.src = preview;
  };

  const handleRemoveOgImage = () => {
    if (ogImagePreview?.startsWith("blob:")) {
      URL.revokeObjectURL(ogImagePreview);
    }
    setOgImageFile(null);
    setOgImagePreview(null);
    setOgImageError(REQUIRED_MSG);
    setForm((prev) => ({ ...prev, og_image: "" }));
  };

  const buildFormData = () => {
    const fd = new FormData();
    Object.entries(form).forEach(([key, value]) => {
      if (key === "og_image") return;
      if (value != null && value !== "") fd.append(key, String(value));
    });
    if (ogImageFile) {
      fd.append("og_image", ogImageFile);
    }
    return fd;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    const errors = {};

    if (!editingId) {
      if (!String(form.entity_type || "").trim()) {
        errors.entity_type = REQUIRED_MSG;
      }
      if (!String(form.entity_id || "").trim()) {
        errors.entity_id = REQUIRED_MSG;
      }
    }

    REQUIRED_TEXT_FIELDS.forEach(({ key }) => {
      if (!String(form[key] || "").trim()) errors[key] = REQUIRED_MSG;
    });

    const hasImage = Boolean(
      ogImageFile || (editingId && ogImagePreview),
    );
    if (!hasImage) {
      setOgImageError(REQUIRED_MSG);
    }

    setFieldErrors(errors);

    if (Object.keys(errors).length > 0 || !hasImage) {
      return;
    }

    if (
      ogImageError &&
      ogImageError !== REQUIRED_MSG &&
      ogImageError !== "OG Image is required."
    ) {
      return;
    }

    setSubmitting(true);
    try {
      const payload = buildFormData();
      if (editingId) {
        await updateSeo(editingId, payload);
        Swal.fire("Updated", "SEO entry updated successfully.", "success");
      } else {
        await createSeo(payload);
        Swal.fire("Created", "SEO entry created successfully.", "success");
      }
      handleClose();
      if (onSuccess) await onSuccess();
    } catch (error) {
      Swal.fire("Error", error.message || "Something went wrong", "error");
    } finally {
      setSubmitting(false);
    }
  };

  const handleModuleChange = (value) => {
    setForm((prev) => ({
      ...prev,
      entity_type: value,
      entity_id: "",
      og_type:
        value === "product" ? "product" : value === "blog" ? "article" : "website",
    }));
    setFieldErrors((prev) => {
      const next = { ...prev };
      delete next.entity_type;
      delete next.entity_id;
      return next;
    });
    setEntitySearch("");
    setSelectedEntityLabel("");
    setEntityOptions([]);
    setEntityTotalCount(0);
    setIsItemDropdownOpen(false);
  };

  if (!isOpen) return null;

  const sectionClass =
    "rounded-2xl border border-[#F0E4F0] bg-gradient-to-b from-[#FDFBFD] to-white p-4 sm:p-5 space-y-4";
  const sectionTitleClass =
    "text-[11px] font-semibold uppercase tracking-[0.18em] text-brand-primary";

  return createPortal(
    <div
      className="fixed inset-0 z-[9999] flex items-center justify-center bg-[#15173D]/55 backdrop-blur-[3px] p-4"
    >
      <div
        className="bg-white w-[680px] max-w-full max-h-[90vh] rounded-[28px] overflow-hidden relative flex flex-col shadow-[0_28px_80px_rgba(21,23,61,0.28)]"
      >
        <div className="brand-gradient px-6 py-5 flex items-start justify-between shrink-0">
          <div>
            <p className="text-white/70 text-[11px] uppercase tracking-[0.22em] font-medium">
              SEO Management
            </p>
            <h3 className="text-white text-[22px] font-semibold mt-1">
              {editingId ? "Edit SEO" : "Add SEO"}
            </h3>
          </div>
          <button
            type="button"
            onClick={handleClose}
            className="text-white/80 hover:text-white hover:bg-white/15 rounded-full p-1.5 transition-colors"
            aria-label="Close"
          >
            <XMarkIcon className="h-6 w-6" />
          </button>
        </div>

        <form
          onSubmit={handleSubmit}
          className="flex flex-col min-h-0 flex-1"
          noValidate
        >
          <div className="flex-1 overflow-y-auto px-6 py-5 space-y-5 seo-modal-panel">
            {!editingId && (
              <section className={sectionClass}>
                <h4 className={sectionTitleClass}>Target page</h4>
                <div className="grid grid-cols-1 gap-4">
                  <div>
                    <label className={labelClass}>
                      Module <span className="text-brand-error">*</span>
                    </label>
                    <SelectField
                      value={form.entity_type}
                      onChange={handleModuleChange}
                      placeholder="Select module"
                      groups={moduleGroups}
                      error={Boolean(fieldErrors.entity_type)}
                    />
                    {fieldErrors.entity_type && (
                      <p className="text-brand-error text-xs mt-1.5">
                        {fieldErrors.entity_type}
                      </p>
                    )}
                  </div>

                  <div ref={itemDropdownRef}>
                    <label className={labelClass}>
                      Item <span className="text-brand-error">*</span>
                    </label>
                    <div className="relative">
                      <FiSearch className="absolute left-4 top-1/2 -translate-y-1/2 text-slate-400 pointer-events-none z-10" />
                      <input
                        type="text"
                        value={
                          isItemDropdownOpen
                            ? entitySearch
                            : selectedEntityLabel
                        }
                        onChange={(e) => {
                          setEntitySearch(e.target.value);
                          setForm((prev) => ({ ...prev, entity_id: "" }));
                          setSelectedEntityLabel("");
                          setIsItemDropdownOpen(true);
                        }}
                        onFocus={() => {
                          if (form.entity_type) setIsItemDropdownOpen(true);
                        }}
                        placeholder={
                          form.entity_type
                            ? "Search and select item..."
                            : "Select a module first"
                        }
                        disabled={!form.entity_type}
                        className={`${fieldClass("entity_id")} pl-11 disabled:bg-slate-50 disabled:text-slate-400 ${
                          isItemDropdownOpen && form.entity_type
                            ? "border-brand-primary ring-2 ring-brand-primary/20"
                            : ""
                        }`}
                        autoComplete="off"
                      />
                    </div>
                    {fieldErrors.entity_id ? (
                      <p className="text-brand-error text-xs mt-1.5">
                        {fieldErrors.entity_id}
                      </p>
                    ) : (
                      <p className="text-slate-400 text-xs mt-1.5">
                        Type to search, then select. Scroll the list to load more.
                      </p>
                    )}
                  </div>
                </div>
              </section>
            )}

            {editingId && (
              <div className="rounded-2xl bg-[#F8EEF8] border border-[#EBD6EB] px-4 py-3 flex flex-wrap items-center gap-2 text-sm">
                <span className="inline-flex items-center rounded-full bg-brand-primary text-white text-[10px] font-semibold uppercase tracking-wider px-2.5 py-1">
                  {form.entity_type}
                </span>
                <span className="font-semibold text-slate-800">
                  {selectedEntityLabel || form.entity_id}
                </span>
              </div>
            )}

            <section className={sectionClass}>
              <h4 className={sectionTitleClass}>Search engine</h4>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="md:col-span-2">
                  <label className={labelClass}>
                    Meta Title <span className="text-brand-error">*</span>
                  </label>
                  <input
                    type="text"
                    value={form.meta_title}
                    onChange={(e) => setField("meta_title", e.target.value)}
                    className={fieldClass("meta_title")}
                    placeholder="Page title for search engines"
                  />
                  {fieldErrors.meta_title && (
                    <p className="text-brand-error text-xs mt-1.5">
                      {fieldErrors.meta_title}
                    </p>
                  )}
                </div>

                <div className="md:col-span-2">
                  <label className={labelClass}>
                    Meta Description <span className="text-brand-error">*</span>
                  </label>
                  <textarea
                    value={form.meta_description}
                    onChange={(e) =>
                      setField("meta_description", e.target.value)
                    }
                    rows={3}
                    className={fieldClass("meta_description", true)}
                    placeholder="Short description for search results"
                  />
                  {fieldErrors.meta_description && (
                    <p className="text-brand-error text-xs mt-1.5">
                      {fieldErrors.meta_description}
                    </p>
                  )}
                </div>

                <div className="md:col-span-2">
                  <label className={labelClass}>
                    Meta Keywords <span className="text-brand-error">*</span>
                  </label>
                  <input
                    type="text"
                    value={form.meta_keywords}
                    onChange={(e) => setField("meta_keywords", e.target.value)}
                    className={fieldClass("meta_keywords")}
                    placeholder="keyword1, keyword2, keyword3"
                  />
                  {fieldErrors.meta_keywords && (
                    <p className="text-brand-error text-xs mt-1.5">
                      {fieldErrors.meta_keywords}
                    </p>
                  )}
                </div>

                <div>
                  <label className={labelClass}>
                    Slug <span className="text-brand-error">*</span>
                  </label>
                  <input
                    type="text"
                    value={form.slug}
                    onChange={(e) => setField("slug", e.target.value)}
                    className={fieldClass("slug")}
                    placeholder="url-friendly-slug"
                  />
                  {fieldErrors.slug && (
                    <p className="text-brand-error text-xs mt-1.5">
                      {fieldErrors.slug}
                    </p>
                  )}
                </div>

                <div>
                  <label className={labelClass}>
                    Canonical URL <span className="text-brand-error">*</span>
                  </label>
                  <input
                    type="text"
                    value={form.canonical_url}
                    onChange={(e) => setField("canonical_url", e.target.value)}
                    className={fieldClass("canonical_url")}
                    placeholder="https://example.com/page"
                  />
                  {fieldErrors.canonical_url && (
                    <p className="text-brand-error text-xs mt-1.5">
                      {fieldErrors.canonical_url}
                    </p>
                  )}
                </div>

                <div>
                  <label className={labelClass}>
                    Robots <span className="text-brand-error">*</span>
                  </label>
                  <SelectField
                    value={form.robots}
                    onChange={(value) => setField("robots", value)}
                    placeholder="Select robots"
                    options={ROBOTS_OPTIONS}
                    error={Boolean(fieldErrors.robots)}
                  />
                  {fieldErrors.robots && (
                    <p className="text-brand-error text-xs mt-1.5">
                      {fieldErrors.robots}
                    </p>
                  )}
                </div>
              </div>
            </section>

            <section className={sectionClass}>
              <h4 className={sectionTitleClass}>Social share</h4>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className={labelClass}>
                    OG Type <span className="text-brand-error">*</span>
                  </label>
                  <SelectField
                    value={form.og_type}
                    onChange={(value) => setField("og_type", value)}
                    placeholder="Select OG type"
                    options={OG_TYPE_OPTIONS}
                    error={Boolean(fieldErrors.og_type)}
                  />
                  {fieldErrors.og_type && (
                    <p className="text-brand-error text-xs mt-1.5">
                      {fieldErrors.og_type}
                    </p>
                  )}
                </div>

                <div className="md:col-span-2">
                  <label className={labelClass}>OG Title</label>
                  <input
                    type="text"
                    value={form.og_title}
                    onChange={(e) => setField("og_title", e.target.value)}
                    className={fieldClass("og_title")}
                    placeholder="Title for social previews"
                  />
                  {fieldErrors.og_title && (
                    <p className="text-brand-error text-xs mt-1.5">
                      {fieldErrors.og_title}
                    </p>
                  )}
                  {!fieldErrors.og_title && (
                    <p className="text-slate-400 text-xs mt-1.5">
                      Optional. Uses meta title if left empty.
                    </p>
                  )}
                </div>

                <div className="md:col-span-2">
                  <label className={labelClass}>
                    OG Image <span className="text-brand-error">*</span>
                  </label>

                  {ogImagePreview ? (
                    <div className="relative group">
                      <div
                        className={`w-full h-44 bg-slate-100 rounded-2xl overflow-hidden border ${
                          ogImageError
                            ? "border-red-400"
                            : "border-[#EBD6EB]"
                        }`}
                      >
                        <ImageWithFallback
                          src={ogImagePreview}
                          alt="OG preview"
                          className="w-full h-full object-cover"
                        />
                      </div>
                      <button
                        type="button"
                        onClick={handleRemoveOgImage}
                        className="absolute top-3 right-3 bg-brand-error hover:bg-red-600 text-white p-2 rounded-full transition-colors shadow-lg"
                        title="Remove image"
                      >
                        <TrashIcon className="h-4 w-4" />
                      </button>
                    </div>
                  ) : (
                    <label
                      htmlFor="og-image-upload"
                      className={`cursor-pointer w-full h-44 bg-[#FBF6FB] border-2 border-dashed rounded-2xl flex flex-col items-center justify-center hover:bg-[#F8EEF8] hover:border-brand-secondary transition-colors ${
                        ogImageError ? "border-red-400" : "border-[#E0C8E0]"
                      }`}
                    >
                      <span className="h-12 w-12 rounded-full bg-white shadow-sm flex items-center justify-center mb-2">
                        <CameraIcon className="h-6 w-6 text-brand-primary" />
                      </span>
                      <span className="text-slate-700 font-semibold text-sm">
                        Upload OG Image
                      </span>
                      <span className="text-brand-error text-xs mt-1 font-semibold">
                        {OG_IMAGE_WIDTH}x{OG_IMAGE_HEIGHT} required
                      </span>
                      <span className="text-slate-400 text-xs mt-1">
                        Prefer JPG/JPEG images
                      </span>
                    </label>
                  )}

                  <input
                    id="og-image-upload"
                    type="file"
                    accept="image/jpeg,.jpg,.jpeg,image/*"
                    onChange={handleOgImageChange}
                    className="hidden"
                  />

                  {ogImageError ? (
                    <p className="text-brand-error text-xs mt-1.5">
                      {ogImageError}
                    </p>
                  ) : (
                    <p className="text-slate-400 text-xs mt-1.5">
                      Required size: {OG_IMAGE_WIDTH}x{OG_IMAGE_HEIGHT}px.
                      Prefer JPG/JPEG.
                    </p>
                  )}
                </div>

                <div className="md:col-span-2">
                  <label className={labelClass}>OG Image Alt</label>
                  <input
                    type="text"
                    value={form.og_image_alt}
                    onChange={(e) => setField("og_image_alt", e.target.value)}
                    className={fieldClass("og_image_alt")}
                    placeholder="Alt text for OG image"
                  />
                  {fieldErrors.og_image_alt && (
                    <p className="text-brand-error text-xs mt-1.5">
                      {fieldErrors.og_image_alt}
                    </p>
                  )}
                  {!fieldErrors.og_image_alt && (
                    <p className="text-slate-400 text-xs mt-1.5">
                      Optional. Uses OG title, then meta title, if left empty.
                    </p>
                  )}
                </div>

                <div className="md:col-span-2">
                  <label className={labelClass}>OG Description</label>
                  <textarea
                    value={form.og_description}
                    onChange={(e) =>
                      setField("og_description", e.target.value)
                    }
                    rows={2}
                    className={fieldClass("og_description", true)}
                    placeholder="Description for social previews"
                  />
                  {fieldErrors.og_description && (
                    <p className="text-brand-error text-xs mt-1.5">
                      {fieldErrors.og_description}
                    </p>
                  )}
                  {!fieldErrors.og_description && (
                    <p className="text-slate-400 text-xs mt-1.5">
                      Optional. Uses meta description if left empty.
                    </p>
                  )}
                </div>
              </div>
            </section>
          </div>

          <div className="px-6 py-4 border-t border-[#F0E4F0] bg-white flex justify-end items-center gap-3 shrink-0">
            <button
              type="button"
              onClick={handleClose}
              className="btn btn-reset-filter justify-center min-w-[108px]"
              disabled={submitting}
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={submitting}
              className={`btn justify-center min-w-[108px] ${
                editingId ? "btn-edit text-white" : "btn-primary"
              } ${submitting ? "opacity-50 cursor-not-allowed" : ""}`}
            >
              {submitting ? "Saving..." : editingId ? "Update" : "Save"}
            </button>
          </div>
        </form>
      </div>

      {isItemDropdownOpen &&
        form.entity_type &&
        createPortal(
          <div
            ref={itemMenuRef}
            style={{
              position: "fixed",
              top: itemMenuCoords.top,
              left: itemMenuCoords.left,
              width: itemMenuCoords.width,
              zIndex: 10050,
            }}
            className="seo-dropdown-menu bg-white rounded-2xl border border-slate-100 overflow-hidden"
            onClick={(e) => e.stopPropagation()}
            onMouseDown={(e) => e.stopPropagation()}
          >
            <div
              className="max-h-64 overflow-y-auto py-1.5"
              onScroll={handleEntityListScroll}
            >
              {entitiesLoading && entityOptions.length === 0 ? (
                <div className="px-4 py-6 text-sm text-slate-400 text-center">
                  Loading items...
                </div>
              ) : entityOptions.length > 0 ? (
                <>
                  {entityOptions.map((item) => {
                    const isSelected =
                      String(form.entity_id) === String(item.id);
                    return (
                      <button
                        key={item.id}
                        type="button"
                        disabled={item.hasSeo}
                        onClick={() => handleSelectItem(item)}
                        className={`w-full flex items-center gap-3 px-3.5 py-2.5 text-left transition-colors ${
                          item.hasSeo
                            ? "opacity-45 cursor-not-allowed bg-slate-50 text-slate-400"
                            : isSelected
                              ? "bg-[#F8EEF8] text-brand-primary"
                              : "text-slate-700 hover:bg-[#FBF6FB]"
                        }`}
                      >
                        <span
                          className={`flex h-[18px] w-[18px] shrink-0 items-center justify-center rounded-full border ${
                            isSelected
                              ? "border-brand-primary bg-brand-primary text-white"
                              : "border-slate-300 bg-white"
                          }`}
                        >
                          {isSelected && <CheckIcon className="h-3 w-3" />}
                        </span>
                        <span className="min-w-0 flex-1 truncate text-sm font-medium">
                          {item.title}
                        </span>
                        {item.hasSeo && (
                          <span className="shrink-0 text-[10px] font-bold uppercase tracking-wide text-brand-primary bg-[#F8EEF8] px-2 py-0.5 rounded-full">
                            SEO added
                          </span>
                        )}
                      </button>
                    );
                  })}
                  {entitiesLoadingMore && (
                    <div className="px-4 py-2.5 text-xs text-brand-primary/70 text-center">
                      Loading more...
                    </div>
                  )}
                  {!entitiesLoadingMore &&
                    entityOptions.length < entityTotalCount && (
                      <div className="px-4 py-2 text-[11px] text-slate-400 text-center">
                        Scroll for more ({entityOptions.length} of{" "}
                        {entityTotalCount})
                      </div>
                    )}
                </>
              ) : (
                <div className="px-4 py-6 text-sm text-slate-400 text-center">
                  No items found
                </div>
              )}
            </div>
          </div>,
          document.body,
        )}
    </div>,
    document.body,
  );
}

export default SeoModal;
